/**
 * QualityMetrics - Comprehensive quality visualization for HI-SI pipeline stages
 * Shows multiple evaluation criteria with thresholds and scores
 */

import { TrendingUp, TrendingDown, Minus, AlertTriangle, CheckCircle } from 'lucide-react';
import React from 'react';

import { cn } from '@/utils/cn';

import './pipeline-animations.css';

export interface QualityScore {
  name: string;
  value: number;
  threshold: number;
  weight?: number;
  isCritical?: boolean;
}

export interface StageQualityMetrics {
  overall: number;
  targetThreshold: number;
  fallbackThreshold: number;
  scores?: QualityScore[];
  passed: boolean;
  isBestAttempt?: boolean;
  trend?: 'improving' | 'stable' | 'declining';
}

interface QualityMetricsProps {
  metrics: StageQualityMetrics;
  showDetails?: boolean;
  compact?: boolean;
  className?: string;
}

const QualityMetrics: React.FC<QualityMetricsProps> = ({
  metrics,
  showDetails = true,
  compact = false,
  className
}) => {
  const getScoreColor = (score: number, threshold: number): string => {
    const percentage = (score / threshold) * 100;
    if (percentage >= 100) return 'text-green-600';
    if (percentage >= 90) return 'text-yellow-600';
    if (percentage >= 75) return 'text-orange-600';
    return 'text-red-600';
  };

  const getScoreBarColor = (score: number, threshold: number): string => {
    const percentage = (score / threshold) * 100;
    if (percentage >= 100) return 'bg-green-500';
    if (percentage >= 90) return 'bg-yellow-500';
    if (percentage >= 75) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const getTrendIcon = () => {
    switch (metrics.trend) {
      case 'improving':
        return <TrendingUp className="w-4 h-4 text-green-500" />;
      case 'declining':
        return <TrendingDown className="w-4 h-4 text-red-500" />;
      default:
        return <Minus className="w-4 h-4 text-gray-500" />;
    }
  };

  const overallPercentage = (metrics.overall / metrics.targetThreshold) * 100;
  const passedTarget = metrics.overall >= metrics.targetThreshold;
  const passedFallback = metrics.overall >= metrics.fallbackThreshold;

  if (compact) {
    // Compact view - just overall score with indicator
    return (
      <div className={cn('flex items-center space-x-2', className)}>
        <div className="flex items-center space-x-1">
          {passedTarget ? (
            <CheckCircle className="w-4 h-4 text-green-500" />
          ) : passedFallback ? (
            <AlertTriangle className="w-4 h-4 text-yellow-500" />
          ) : (
            <AlertTriangle className="w-4 h-4 text-red-500" />
          )}
          <span className={cn(
            'text-sm font-medium',
            getScoreColor(metrics.overall, metrics.targetThreshold)
          )}>
            {Math.round(overallPercentage)}%
          </span>
        </div>
        {metrics.isBestAttempt && (
          <span className="text-xs text-yellow-600" title="Using best attempt">
            ⭐
          </span>
        )}
        {getTrendIcon()}
      </div>
    );
  }

  return (
    <div className={cn('space-y-4', className)}>
      {/* Overall Score with Threshold Indicators */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-gray-700">Overall Quality</span>
            {metrics.isBestAttempt && (
              <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded-full font-medium">
                Best Attempt
              </span>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <span className={cn(
              'text-lg font-bold',
              getScoreColor(metrics.overall, metrics.targetThreshold)
            )}>
              {Math.round(overallPercentage)}%
            </span>
            {getTrendIcon()}
          </div>
        </div>

        {/* Quality Bar with Thresholds */}
        <div className="relative">
          <div className="quality-bar relative h-8 bg-gradient-to-r from-red-500 via-yellow-500 to-green-500 rounded-lg overflow-hidden">
            {/* Background gradient overlay for visual appeal */}
            <div className="absolute inset-0 bg-black opacity-10" />
            
            {/* Fallback threshold marker */}
            <div
              className="quality-threshold absolute top-0 bottom-0 w-0.5 bg-gray-800 opacity-50"
              style={{ left: `${(metrics.fallbackThreshold / 1.0) * 100}%` }}
              title={`Fallback: ${(metrics.fallbackThreshold * 100).toFixed(0)}%`}
            >
              <span className="absolute -top-5 left-1/2 transform -translate-x-1/2 text-xs text-gray-600">
                Min
              </span>
            </div>

            {/* Target threshold marker */}
            <div
              className="quality-threshold absolute top-0 bottom-0 w-0.5 bg-gray-900"
              style={{ left: `${(metrics.targetThreshold / 1.0) * 100}%` }}
              title={`Target: ${(metrics.targetThreshold * 100).toFixed(0)}%`}
            >
              <span className="absolute -top-5 left-1/2 transform -translate-x-1/2 text-xs text-gray-700 font-medium">
                Target
              </span>
            </div>

            {/* Current score indicator */}
            <div
              className="quality-score absolute top-1/2 transform -translate-y-1/2 w-3 h-3 bg-white border-2 border-gray-900 rounded-full shadow-lg transition-all duration-500"
              style={{ left: `${Math.min(100, Math.max(0, metrics.overall * 100))}%` }}
            >
              <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 text-xs font-bold text-gray-900 whitespace-nowrap">
                {(metrics.overall * 100).toFixed(1)}%
              </div>
            </div>
          </div>
        </div>

        {/* Pass/Fail Status */}
        <div className="flex items-center justify-center">
          {passedTarget ? (
            <div className="flex items-center space-x-1 text-green-600">
              <CheckCircle className="w-4 h-4" />
              <span className="text-sm font-medium">Passed Target Threshold</span>
            </div>
          ) : passedFallback ? (
            <div className="flex items-center space-x-1 text-yellow-600">
              <AlertTriangle className="w-4 h-4" />
              <span className="text-sm font-medium">Passed Minimum Threshold</span>
            </div>
          ) : (
            <div className="flex items-center space-x-1 text-red-600">
              <AlertTriangle className="w-4 h-4" />
              <span className="text-sm font-medium">Below Minimum Threshold</span>
            </div>
          )}
        </div>
      </div>

      {/* Individual Score Breakdown */}
      {showDetails && metrics.scores && metrics.scores.length > 0 && (
        <div className="space-y-2">
          <div className="space-y-2">
            {metrics.scores.map((score, index) => {
              const percentage = (score.value / score.threshold) * 100;
              const passed = score.value >= score.threshold;
              
              return (
                <div key={index} className="space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-gray-600 capitalize">
                        {score.name.replace(/_/g, ' ')}
                      </span>
                      {score.isCritical && (
                        <span className="px-1 py-0.5 bg-red-100 text-red-700 text-xs rounded">
                          Critical
                        </span>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={cn(
                        'font-medium',
                        passed ? 'text-green-600' : 'text-red-600'
                      )}>
                        {score.value.toFixed(2)}
                      </span>
                      <span className="text-gray-400">/ {score.threshold.toFixed(2)}</span>
                      {passed ? (
                        <CheckCircle className="w-3 h-3 text-green-500" />
                      ) : (
                        <AlertTriangle className="w-3 h-3 text-red-500" />
                      )}
                    </div>
                  </div>
                  
                  {/* Individual score bar */}
                  <div className="relative h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={cn(
                        'absolute left-0 top-0 h-full transition-all duration-500',
                        getScoreBarColor(score.value, score.threshold)
                      )}
                      style={{ width: `${Math.min(100, percentage)}%` }}
                    />
                    {/* Threshold marker */}
                    <div
                      className="absolute top-0 bottom-0 w-0.5 bg-gray-600 opacity-50 right-0"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Anti-Cliché Indicators (for vision/shots stages) */}
      {metrics.scores && metrics.scores.some(s => s.name === 'cringe_score' || s.name === 'anti_cliche') && (
        <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
          <div className="flex items-center space-x-2">
            <span className="text-purple-600">🚫</span>
            <span className="text-sm font-medium text-purple-900">
              Anti-Cliché System Active
            </span>
          </div>
          {metrics.scores
            .filter(s => s.name === 'cringe_score' || s.name === 'anti_cliche')
            .map((score, index) => (
              <div key={index} className="mt-2 text-xs text-purple-700">
                {score.name === 'cringe_score' 
                  ? `Cringe Score: ${score.value.toFixed(1)}/10 (max allowed: ${score.threshold})`
                  : `Anti-Cliché Score: ${(score.value * 100).toFixed(0)}%`
                }
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

export default QualityMetrics;